import { useEffect, useState } from 'react';
import { Admin } from '../../api.js';
import { FormField, Switch } from '../../components/FormField.jsx';
import { PageHeader } from '../../components/PageHeader.jsx';
import { Modal, ConfirmDialog, submitForm } from '../../components/Modal.jsx';
import { EmptyState } from './Teams.jsx';

const TYPES = [
  { v: 'text', l: 'Short text' },
  { v: 'textarea', l: 'Long text' },
  { v: 'email', l: 'Email' },
  { v: 'tel', l: 'Phone' },
  { v: 'number', l: 'Number' },
  { v: 'date', l: 'Date' },
  { v: 'url', l: 'URL' },
  { v: 'select', l: 'Select (dropdown)' },
  { v: 'checkbox', l: 'Checkbox' },
];

const empty = {
  key: '',
  label: '',
  type: 'text',
  required: false,
  placeholder: '',
  helpText: '',
  options: [],
  active: true,
};

export default function SignupFields() {
  const [fields, setFields] = useState([]);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState(empty);
  const [optionsRaw, setOptionsRaw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function refresh() {
    const r = await Admin.signupFields.list();
    setFields(r.fields);
  }
  useEffect(() => { refresh(); }, []);

  function openCreate() {
    setForm(empty);
    setOptionsRaw('');
    setErr(null);
    setModal('create');
  }
  function openEdit(f) {
    setForm({
      key: f.key,
      label: f.label,
      type: f.type,
      required: !!f.required,
      placeholder: f.placeholder || '',
      helpText: f.helpText || '',
      options: f.options || [],
      active: !!f.active,
    });
    setOptionsRaw((f.options || []).join('\n'));
    setErr(null);
    setModal({ id: f.id });
  }

  async function save(e) {
    e.preventDefault();
    setErr(null);
    // Pre-flight client-side checks with clear messages.
    if (!form.label.trim()) {
      setErr('Give the field a label — that\'s what members will see.');
      return;
    }
    const options = form.type === 'select'
      ? optionsRaw.split('\n').map((s) => s.trim()).filter(Boolean)
      : [];
    if (form.type === 'select' && options.length === 0) {
      setErr('Add at least one option (one per line) for a select field.');
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, options };
      if (modal === 'create') {
        if (!payload.key) delete payload.key;
        await Admin.signupFields.create(payload);
      } else {
        // Key is immutable after creation — strip it from the update.
        delete payload.key;
        await Admin.signupFields.update(modal.id, payload);
      }
      setModal(null);
      await refresh();
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally { setBusy(false); }
  }

  async function remove(id) {
    await Admin.signupFields.remove(id);
    await refresh();
  }

  async function move(i, dir) {
    const newOrder = [...fields];
    const j = i + dir;
    if (j < 0 || j >= newOrder.length) return;
    [newOrder[i], newOrder[j]] = [newOrder[j], newOrder[i]];
    setFields(newOrder);
    await Admin.signupFields.reorder(newOrder.map((f) => f.id));
  }

  async function toggleActive(f) {
    await Admin.signupFields.update(f.id, { active: !f.active });
    await refresh();
  }

  return (
    <div>
      <PageHeader
        eyebrow="Signup"
        title="Custom fields"
        description="Extra questions shown on the public signup form. Values land on each user's profile and contact record."
        action={<button onClick={openCreate} className="btn-primary btn-sm">+ New field</button>}
      />

      {fields.length === 0 ? (
        <EmptyState onAction={openCreate} label="Add a field" />
      ) : (
        <div className="card-flush">
          <div className="hidden md:grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 items-center px-5 py-3 border-b border-line">
            <div className="eyebrow w-5"></div>
            <div className="eyebrow">Label / key</div>
            <div className="eyebrow">Type</div>
            <div className="eyebrow">Required</div>
            <div className="eyebrow">Active</div>
            <div className="eyebrow"></div>
          </div>
          <div className="divide-rule">
            {fields.map((f, i) => (
              <div
                key={f.id}
                className={`px-5 py-4 md:grid md:grid-cols-[auto_1fr_auto_auto_auto_auto] md:gap-4 md:items-center ${!f.active ? 'opacity-60' : ''}`}
              >
                <div className="hidden md:flex flex-col items-center w-5 gap-0.5">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="text-ink-3 hover:text-ink disabled:opacity-30 text-xs"
                    aria-label="Move up"
                  >▲</button>
                  <button
                    onClick={() => move(i, 1)}
                    disabled={i === fields.length - 1}
                    className="text-ink-3 hover:text-ink disabled:opacity-30 text-xs"
                    aria-label="Move down"
                  >▼</button>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{f.label}</div>
                  <div className="text-[11px] text-ink-3 mt-0.5 font-mono truncate">{f.key}</div>
                  {f.helpText && <div className="text-xs text-ink-3 mt-1 italic">{f.helpText}</div>}
                </div>
                <div className="hidden md:block">
                  <span className="chip-neutral">{TYPES.find((t) => t.v === f.type)?.l || f.type}</span>
                </div>
                <div className="hidden md:block text-xs text-ink-2">
                  {f.required ? 'Yes' : '—'}
                </div>
                <div className="hidden md:block">
                  <button onClick={() => toggleActive(f)} className={f.active ? 'chip-success' : 'chip-neutral'}>
                    {f.active ? 'On' : 'Off'}
                  </button>
                </div>
                <div className="flex gap-1 md:justify-end mt-2 md:mt-0">
                  <button className="btn-outline btn-xs" onClick={() => openEdit(f)}>Edit</button>
                  <button className="btn-ghost btn-xs text-danger" onClick={() => setConfirm(f)}>Del</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal
        open={!!modal}
        onClose={() => setModal(null)}
        eyebrow={modal === 'create' ? 'New field' : 'Edit field'}
        title={modal === 'create' ? 'Add signup field' : form.label || 'Edit'}
        size="lg"
        footer={
          <>
            <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
            <button type="button" onClick={() => submitForm('signup-field-form')} className="btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {err && <div className="mb-4 px-3 py-2 rounded-md bg-danger/10 text-danger text-sm">{err}</div>}
        <form id="signup-field-form" onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Label" required hint="Shown on the signup form">
              <input
                className="input"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                autoFocus
              />
            </FormField>
            <FormField label="Type" required>
              <select
                className="input"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
              >
                {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </FormField>
          </div>
          {modal === 'create' ? (
            <FormField
              label="Key"
              hint="Used as the field name in the database. Auto-derived from the label if left blank. Immutable after creation."
            >
              <input
                className="input font-mono text-xs"
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                placeholder="auto"
              />
            </FormField>
          ) : (
            <div className="text-[11px] text-ink-3">Key: <code className="font-mono">{form.key}</code> (immutable)</div>
          )}
          <FormField label="Placeholder" hint="Greyed-out hint inside the input">
            <input
              className="input"
              value={form.placeholder}
              onChange={(e) => setForm({ ...form, placeholder: e.target.value })}
            />
          </FormField>
          <FormField label="Help text" hint="Small grey text under the input">
            <input
              className="input"
              value={form.helpText}
              onChange={(e) => setForm({ ...form, helpText: e.target.value })}
            />
          </FormField>
          {form.type === 'select' && (
            <FormField label="Options" required hint="One per line">
              <textarea
                className="input font-mono text-xs"
                rows={5}
                value={optionsRaw}
                onChange={(e) => setOptionsRaw(e.target.value)}
                placeholder={`Option 1\nOption 2\nOption 3`}
              />
            </FormField>
          )}
          <div className="flex flex-col gap-3 pt-2 border-t border-line">
            <Switch
              checked={form.required}
              onChange={(v) => setForm({ ...form, required: v })}
              label="Required"
              hint="Signup form won't submit without a value"
            />
            <Switch
              checked={form.active}
              onChange={(v) => setForm({ ...form, active: v })}
              label="Active"
              hint="Off = hidden from signup, existing data kept"
            />
          </div>
          {form.label && (
            <div className="pt-3 border-t border-line">
              <div className="eyebrow mb-2">Live preview</div>
              <div className="rounded-md border border-line bg-canvas/50 p-4">
                <FieldPreview field={{ ...form, options: form.type === 'select' ? optionsRaw.split('\n').map((s) => s.trim()).filter(Boolean) : [] }} />
              </div>
            </div>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove(confirm.id)}
        title={`Delete "${confirm?.label}"?`}
        message="The field is removed from the signup form. Existing user data with this key is preserved."
        confirmLabel="Delete"
        danger
      />
    </div>
  );
}

function FieldPreview({ field }) {
  if (field.type === 'checkbox') {
    return <Switch checked={false} onChange={() => {}} label={field.label} hint={field.helpText} />;
  }
  if (field.type === 'textarea') {
    return <FormField label={field.label} required={field.required} hint={field.helpText}>
      <textarea className="input" rows={3} placeholder={field.placeholder} disabled />
    </FormField>;
  }
  if (field.type === 'select') {
    return <FormField label={field.label} required={field.required} hint={field.helpText}>
      <select className="input" disabled>
        <option>{field.placeholder || `Select ${field.label.toLowerCase()}…`}</option>
        {(field.options || []).map((o) => <option key={o}>{o}</option>)}
      </select>
    </FormField>;
  }
  return <FormField label={field.label} required={field.required} hint={field.helpText}>
    <input className="input" type={field.type} placeholder={field.placeholder} disabled />
  </FormField>;
}
